/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.13.0
 */

import styled from 'styled-components';

import { IAttrStyled } from '@/main/types/globalTypes';
import { colors, fonts } from '@theme/index';

export const SubscriberIdDcRulesStyled = styled.div.attrs({
    className: 'subscriber-dc-rules',
})`
    // common styles

    .subscriber-dc-rules__block {
        margin-bottom: 48px;
    }

    .subscriber-dc-rules__title-block {
        margin-bottom: 24px;

        .subscriber-dc-rules__sub-title {
            margin-top: 4px;
        }
    }

    // specific block styles

    .subscriber-dc-rules__rule {
        margin-bottom: 16px;
        padding: 20px;
        border: 1px solid ${colors.borderColorDisabled};
        border-radius: 4px;
    }

    .subscriber-dc-rules__rule-header {
        display: flex;
        justify-content: space-between;
    }

    .subscriber-dc-rules__date-block {
        display: inline-grid;
        grid-template-columns: repeat(3, auto);
        gap: 8px;
        margin-top: 8px;
    }

    .subscriber-dc-rules__rule-date {
        display: flex;
        align-items: center;
        position: relative;

        &:not(:last-of-type) {
            margin-bottom: 8px;
        }

        &:before,
        &:after {
            content: '';
            position: relative;
            margin-right: 16px;
            background-color: ${colors.backgroundColorDisabledDark};
        }

        &:before {
            width: 10px;
            height: 10px;
            border-radius: 100%;
        }

        &:not(:last-of-type):after {
            position: absolute;
            top: 17px;
            left: 4px;
            width: 2px;
            height: 100%;
        }

        &.subscriber-dc-rules__rule-date_active {
            &:before {
                background-color: ${colors.backgroundColorSuccess};
            }
        }

        .rule-date__time {
            min-width: 210px;
            margin-right: 16px;
            color: ${colors.textColorOpacity};
        }
    }

    /* accordion */

    .subscriber-dc-rules__accordion-list {
        width: auto;

        .subscriber-dc-rules-accordion__title,
        .subscriber-dc-rules-accordion__head {
            display: grid;
            grid-template-columns: 1fr repeat(3, 75px);
            column-gap: 12px;

            & > *:first-of-type {
                margin-right: 24px;
            }
        }

        .subscriber-dc-rules-accordion__head {
            margin: 0 54px 12px 20px;
            ${fonts.textMainBold};
        }
    }

    /* Notification */

    .subscriber-dc-rules__notification {
        .subscriber-id__title {
            margin-bottom: 12px;
        }
    }

    .subscriber-dc-rules__notification-list {
        display: grid;
        grid-template-columns: 1fr 1fr;
        row-gap: 16px;
    }

    /* Schedule */

    .subscriber-dc-rules__schedule-list {
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 12px 4px;
    }

    .subscriber-dc-rules__schedule-date {
        ${fonts.textMainBold};
    }

    .subscriber-dc-rules__schedule-separate {
        border-bottom: 1px dotted ${colors.borderColorSeparate};
    }
`;

interface ISubscriberIdDcRules extends IAttrStyled {
    level: number;
}

export const SubscriberIdDcRulesAccordionStyled = styled.div.attrs(({ className, level }: ISubscriberIdDcRules) => ({
    className: `subscriber-dc-rules-accordion accordion-level-${level} ${className || ''}`,
}))<{ level: number } & IAttrStyled>`
    padding-left: ${(props) => props.level * 24}px;

    .subscriber-dc-rules-accordion__name {
        display: flex;
        align-items: center;
    }

    .subscriber-dc-rules-accordion__icon {
        margin-right: 12px;
        height: 32px;
    }

    &.accordion_no-child {
        .collapse__panel-title {
            padding-right: 52px;
        }
    }

    .collapse__panel-title {
        ${fonts.textMain};

        &.opened {
            background-color: white;
        }
    }

    .collapse__panel-title,
    .collapse__panel-body {
        border-bottom: none;
    }
`;
